import React, { useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FilterPanel from "../common/FilterPanel";
import FilterChips, { countActive } from "../common/FilterChips";
import { STATUS_REPORT_FILTERS } from "../../config/filterConfigs";
import {
  faSearch, faTrash, faPaperPlane, faFlag, faCheck, faTimes,
  faSpinner, faRefresh, faChevronLeft, faChevronRight,
  faShield, faEye, faCommentDots,
  faStar, faLightbulb, faBug, faChartBar,
} from "@fortawesome/free-solid-svg-icons";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const REASON_LABELS = {
  inappropriate: "Inappropriate",
  spam: "Spam",
  fake_profile: "Fake Profile",
  harassment: "Harassment",
  other: "Other",
};

const STATUS_BADGE = {
  pending:   { label: "Pending",   cls: "bg-yellow-100 text-yellow-700" },
  dismissed: { label: "Dismissed", cls: "bg-slate-100 text-slate-600" },
  cleared:   { label: "Cleared",   cls: "bg-red-100 text-red-600" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

// ─── Shared sub-components ────────────────────────────────────────────────────

function TabBar({ tab, setTab }) {
  return (
    <div className="flex border-b border-slate-200 mb-6 overflow-x-auto">
      {[
        { id: "qa",       label: "QA Tool",       icon: faShield },
        { id: "reports",  label: "Reports Queue",  icon: faFlag },
        { id: "feedback", label: "User Feedback",  icon: faCommentDots },
      ].map(({ id, label, icon }) => (
        <button
          key={id}
          onClick={() => setTab(id)}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
            tab === id
              ? "border-indigo-500 text-indigo-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <FontAwesomeIcon icon={icon} />
          {label}
        </button>
      ))}
    </div>
  );
}

// ─── QA Tool tab ─────────────────────────────────────────────────────────────

function QATool() {
  const [searchQuery, setSearchQuery] = useState("");
  const [profiles, setProfiles]       = useState([]);
  const [searching, setSearching]     = useState(false);
  const [selected, setSelected]       = useState(null);
  const [statusText, setStatusText]   = useState("");
  const [posting, setPosting]         = useState(false);
  const [clearing, setClearing]       = useState(false);

  const searchProfiles = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSelected(null);
    try {
      const res = await func.get(`/matchmatrix/admin/status/search-profile?q=${encodeURIComponent(searchQuery.trim())}`);
      if (res.data?.success) {
        setProfiles(res.data.data ?? []);
        if ((res.data.data ?? []).length === 0) toast.info("No profiles found.");
      } else {
        toast.error(res.data?.message ?? "Search failed.");
      }
    } catch {
      toast.error("Search failed.");
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  const postStatus = async () => {
    if (!selected) return;
    if (!statusText.trim()) { toast.warning("Enter a status text."); return; }
    setPosting(true);
    try {
      const res = await func.post("/matchmatrix/admin/status/post", {
        bgProfileId: selected._id,
        statusUpdate: statusText.trim(),
        statusType: "text",
      });
      if (res.data?.success) {
        toast.success("Status posted.");
        setSelected({ ...selected, statusUpdate: statusText.trim(), statusType: "text" });
        setStatusText("");
      } else {
        toast.error(res.data?.message ?? "Failed to post status.");
      }
    } catch {
      toast.error("Failed to post status.");
    } finally {
      setPosting(false);
    }
  };

  const clearStatus = async () => {
    if (!selected) return;
    setClearing(true);
    try {
      const res = await func.del(`/matchmatrix/admin/status/profile/${selected._id}`);
      if (res.data?.success) {
        toast.success("Status cleared.");
        setSelected({ ...selected, statusUpdate: null, statusType: "text" });
      } else {
        toast.error(res.data?.message ?? "Failed to clear status.");
      }
    } catch {
      toast.error("Failed to clear status.");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Search Profile</h2>
        <div className="flex gap-2">
          <input
            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            placeholder="Name, mobile number, or profile ID…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchProfiles()}
          />
          <button
            onClick={searchProfiles}
            disabled={searching}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
          >
            {searching ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSearch} />}
            Search
          </button>
        </div>

        {profiles.length > 0 && (
          <div className="mt-3 divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {profiles.map((p) => (
              <button
                key={p._id}
                onClick={() => setSelected(p)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-indigo-50 transition-colors ${selected?._id === p._id ? "bg-indigo-50" : ""}`}
              >
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">
                  {(p.firstName?.[0] ?? "?").toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-slate-800">{p.firstName} {p.lastName}</div>
                  <div className="text-xs text-slate-500">{p._id}</div>
                </div>
                {p.statusUpdate && (
                  <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Has Status</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected profile action panel */}
      {selected && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
              {(selected.firstName?.[0] ?? "?").toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-slate-800">{selected.firstName} {selected.lastName}</div>
              <div className="text-xs text-slate-500 font-mono">{selected._id}</div>
            </div>
          </div>

          {/* Current status */}
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-xs text-slate-500 font-medium mb-1">Current Status</div>
            {selected.statusUpdate ? (
              <div className="text-sm text-slate-800">"{selected.statusUpdate}"
                <span className="ml-2 text-xs text-slate-400">({selected.statusType})</span>
              </div>
            ) : (
              <div className="text-sm text-slate-400 italic">No active status</div>
            )}
          </div>

          {/* Post new status */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Post Test Status</label>
            <div className="flex gap-2">
              <input
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                placeholder="Type status text (max 200 chars)…"
                maxLength={200}
                value={statusText}
                onChange={(e) => setStatusText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && postStatus()}
              />
              <button
                onClick={postStatus}
                disabled={posting || !statusText.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
              >
                {posting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
                Post
              </button>
            </div>
          </div>

          {/* Clear status */}
          {selected.statusUpdate && (
            <div className="flex justify-end">
              <button
                onClick={clearStatus}
                disabled={clearing}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 disabled:opacity-60"
              >
                {clearing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrash} />}
                Clear Status
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Reports Queue tab ────────────────────────────────────────────────────────

function ReportsQueue() {
  const [reports, setReports]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);
  const [filters, setFilters]       = useState({ status: "pending" });
  const [showFilter, setShowFilter] = useState(false);
  const [reload, setReload]         = useState(false);
  const [actioningId, setActId]     = useState(null);
  const [reviewNote, setNote]     = useState({});
  const LIMIT = 20;

  const fetchReports = useCallback(async (pg = page) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: pg, limit: LIMIT, status: filters.status || "all" });
      if (filters.reason) qs.append("reason", filters.reason);
      if (filters.startDate) qs.append("startDate", filters.startDate);
      if (filters.endDate) qs.append("endDate", filters.endDate);
      const res = await func.get(`/matchmatrix/admin/status/reports?${qs}`);
      if (res.data?.success) {
        setReports(res.data.data ?? []);
        setTotal(res.data.pagination?.total ?? 0);
      } else {
        toast.error(res.data?.message ?? "Failed to load reports.");
      }
    } catch {
      toast.error("Failed to load reports.");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    fetchReports(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, reload]);

  const action = async (reportId, act) => {
    setActId(reportId);
    try {
      const res = await func.put(`/matchmatrix/admin/status/reports/${reportId}`, {
        action: act,
        reviewNote: reviewNote[reportId] ?? null,
      });
      if (res.data?.success) {
        toast.success(res.data.message);
        setReports((prev) => prev.filter((r) => r._id !== reportId));
        setTotal((t) => Math.max(0, t - 1));
      } else {
        toast.error(res.data?.message ?? "Action failed.");
      }
    } catch {
      toast.error("Action failed.");
    } finally {
      setActId(null);
    }
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-4">
      {/* Filter + refresh bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => setShowFilter(true)} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition">
            Filters
            {countActive(STATUS_REPORT_FILTERS, filters) > 0 && (<span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-indigo-600 text-white rounded-full w-5 h-5">{countActive(STATUS_REPORT_FILTERS, filters)}</span>)}
          </button>
          <FilterChips config={STATUS_REPORT_FILTERS} filters={filters} onClearKeys={(keys) => { const n = { ...filters }; keys.forEach((k) => { n[k] = ""; }); setFilters(n); setPage(1); setReload((r) => !r); }} onClearAll={() => { setFilters({}); setPage(1); setReload((r) => !r); }} />
        </div>
        <button onClick={() => fetchReports(page)} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-600">
          <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
        </button>
      </div>
      <FilterPanel isOpen={showFilter} onClose={() => setShowFilter(false)} title="Filter Reports" config={STATUS_REPORT_FILTERS} filters={filters} onChange={setFilters} onApply={() => { setPage(1); setReload((r) => !r); }} onReset={() => { setFilters({}); setPage(1); setReload((r) => !r); }} />

      {/* Total count */}
      <div className="text-xs text-slate-500">{total} report{total !== 1 ? "s" : ""} found</div>

      {/* Report cards */}
      {loading && reports.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <FontAwesomeIcon icon={faSpinner} spin className="text-2xl" />
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          <FontAwesomeIcon icon={faCheck} className="text-3xl mb-2 block" />
          No reports to show.
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => {
            const badge = STATUS_BADGE[r.status] ?? STATUS_BADGE.pending;
            const profile = r.reportedProfile;
            return (
              <div key={r._id} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                {/* Header row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-xs">
                      {(profile?.firstName?.[0] ?? "?").toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800 text-sm">
                        {profile ? `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim() : "Unknown Profile"}
                      </div>
                      <div className="text-xs text-slate-400 font-mono">{r.reportedProfileId}</div>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                </div>

                {/* Reason + details */}
                <div className="flex flex-wrap gap-2">
                  <span className="flex items-center gap-1 text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full font-medium">
                    <FontAwesomeIcon icon={faFlag} />
                    {REASON_LABELS[r.reason] ?? r.reason}
                  </span>
                  {r.details && (
                    <span className="text-xs text-slate-500 italic">"{r.details}"</span>
                  )}
                </div>

                {/* Status snapshot */}
                {r.statusSnapshot?.statusUpdate && (
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                    <div className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1">
                      <FontAwesomeIcon icon={faEye} /> Status at time of report
                    </div>
                    <div className="text-sm text-slate-700">
                      "{r.statusSnapshot.statusUpdate}"
                    </div>
                    {r.statusSnapshot.statusUpdatedAt && (
                      <div className="text-xs text-slate-400 mt-1">Posted: {fmtDate(r.statusSnapshot.statusUpdatedAt)}</div>
                    )}
                  </div>
                )}

                <div className="text-xs text-slate-400">Reported: {fmtDate(r.createdAt)}</div>

                {/* Action area (only for pending) */}
                {r.status === "pending" && (
                  <div className="border-t border-slate-100 pt-3 space-y-2">
                    <input
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      placeholder="Review note (optional)…"
                      value={reviewNote[r._id] ?? ""}
                      onChange={(e) => setNote((prev) => ({ ...prev, [r._id]: e.target.value }))}
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        disabled={actioningId === r._id}
                        onClick={() => action(r._id, "dismiss")}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-60"
                      >
                        {actioningId === r._id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTimes} />}
                        Dismiss
                      </button>
                      <button
                        disabled={actioningId === r._id}
                        onClick={() => action(r._id, "clear_and_dismiss")}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
                      >
                        {actioningId === r._id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrash} />}
                        Clear & Dismiss
                      </button>
                    </div>
                  </div>
                )}

                {/* Reviewed info */}
                {r.status !== "pending" && r.reviewedAt && (
                  <div className="text-xs text-slate-400 border-t border-slate-100 pt-2">
                    Actioned: {fmtDate(r.reviewedAt)}
                    {r.reviewNote && <span className="ml-2 italic">"{r.reviewNote}"</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="text-sm text-slate-600">Page {page} of {pages}</span>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Feedback Queue tab ───────────────────────────────────────────────────────

const FEATURE_LABELS = {
  video_status:              "🎥 Video Status",
  voice_note_status:         "🎙️ Voice Note Status",
  chat_messaging:            "💬 Chat & Messaging",
  video_call:                "📞 Video Call",
  profile_verification_badge:"✅ Verification Badge",
  family_profile_section:    "👨‍👩‍👧 Family Profile",
  events_meetups:            "🎉 Events & Meetups",
  read_receipts:             "👁️ Read Receipts",
  ai_match_explanation:      "🤖 AI Match Explanation",
  regional_language_support: "🌏 Regional Languages",
  compatibility_deep_dive:   "📊 Compatibility Deep Dive",
  private_mode:              "🔒 Private Mode",
  astrology_matching:        "⭐ Astrology Matching",
  other:                     "💭 Other",
};

const CAT_META = {
  feature_request: { label: "Feature Request", icon: faLightbulb,    color: "text-indigo-600", bg: "bg-indigo-50" },
  match_quality:   { label: "Match Quality",   icon: faStar,          color: "text-amber-600",  bg: "bg-amber-50"  },
  bug_report:      { label: "Bug Report",      icon: faBug,           color: "text-red-600",    bg: "bg-red-50"    },
  general:         { label: "General",          icon: faCommentDots,   color: "text-slate-600",  bg: "bg-slate-100" },
};

const STATUS_META = {
  new:          { label: "New",          cls: "bg-blue-100 text-blue-700"    },
  acknowledged: { label: "Acknowledged", cls: "bg-yellow-100 text-yellow-700"},
  planned:      { label: "Planned",      cls: "bg-indigo-100 text-indigo-700"},
  shipped:      { label: "Shipped",      cls: "bg-emerald-100 text-emerald-700"},
  declined:     { label: "Declined",     cls: "bg-slate-100 text-slate-500"  },
};

function FeedbackQueue() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const [statusFilter, setFilter] = useState("new");
  const [catFilter, setCat]     = useState("all");
  const [featureCounts, setFC]  = useState([]);
  const [avgRating, setAvgRating] = useState(null);
  const [actioningId, setActId] = useState(null);
  const [notes, setNotes]       = useState({});
  const [actionStatus, setActionStatus] = useState({});
  const LIMIT = 20;

  const fetchFeedback = useCallback(async (pg = page, sf = statusFilter, cf = catFilter) => {
    setLoading(true);
    try {
      const res = await func.get(
        `/matchmatrix/admin/feedback?page=${pg}&limit=${LIMIT}&status=${sf}&category=${cf}`
      );
      if (res.data?.success) {
        setItems(res.data.data ?? []);
        setTotal(res.data.pagination?.total ?? 0);
        setFC(res.data.meta?.featureCounts ?? []);
        setAvgRating(res.data.meta?.avgRating ?? null);
      } else {
        toast.error(res.data?.message ?? "Failed to load feedback.");
      }
    } catch {
      toast.error("Failed to load feedback.");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, catFilter]);

  React.useEffect(() => { fetchFeedback(page, statusFilter, catFilter); }, [page, statusFilter, catFilter]); // eslint-disable-line

  const updateFeedback = async (id) => {
    const newStatus = actionStatus[id];
    if (!newStatus) { toast.warning("Select a status first."); return; }
    setActId(id);
    try {
      const res = await func.put(`/matchmatrix/admin/feedback/${id}`, {
        adminStatus: newStatus,
        adminNote: notes[id] ?? null,
      });
      if (res.data?.success) {
        toast.success("Feedback updated.");
        setItems((prev) => prev.map((x) => x._id === id ? (res.data.data ?? { ...x, adminStatus: newStatus, adminNote: notes[id] ?? x.adminNote }) : x));
      } else {
        toast.error(res.data?.message ?? "Update failed.");
      }
    } catch {
      toast.error("Update failed.");
    } finally {
      setActId(null);
    }
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-5">
      {/* Top summary row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">{total}</div>
          <div className="text-xs text-slate-500 mt-0.5">Total Responses</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-amber-500">
            {avgRating ? `${avgRating} ★` : "—"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Avg. Rating</div>
        </div>
        {featureCounts.slice(0, 2).map((fc) => (
          <div key={fc._id} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className="text-2xl font-bold text-indigo-600">{fc.count}</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">
              {FEATURE_LABELS[fc._id] ?? fc._id}
            </div>
          </div>
        ))}
      </div>

      {/* Top feature requests chart */}
      {featureCounts.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <FontAwesomeIcon icon={faChartBar} className="text-indigo-500" />
            <h3 className="text-sm font-semibold text-slate-700">Most Requested Features</h3>
          </div>
          <div className="space-y-2">
            {featureCounts.slice(0, 8).map((fc) => {
              const max = featureCounts[0]?.count ?? 1;
              const pct = Math.round((fc.count / max) * 100);
              return (
                <div key={fc._id} className="flex items-center gap-3">
                  <div className="w-40 text-xs text-slate-600 truncate shrink-0">
                    {FEATURE_LABELS[fc._id] ?? fc._id}
                  </div>
                  <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-indigo-500 h-full rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-xs font-semibold text-slate-700 w-8 text-right">{fc.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap gap-2">
          {["new", "acknowledged", "planned", "shipped", "declined", "all"].map((f) => (
            <button
              key={f}
              onClick={() => { setFilter(f); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                statusFilter === f
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-indigo-400"
              }`}
            >
              {(STATUS_META[f]?.label ?? "All")}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={catFilter}
            onChange={(e) => { setCat(e.target.value); setPage(1); }}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="all">All categories</option>
            {Object.entries(CAT_META).map(([v, m]) => (
              <option key={v} value={v}>{m.label}</option>
            ))}
          </select>
          <button
            onClick={() => fetchFeedback(page, statusFilter, catFilter)}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-600"
          >
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
        </div>
      </div>

      <div className="text-xs text-slate-500">{total} response{total !== 1 ? "s" : ""}</div>

      {/* Cards */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <FontAwesomeIcon icon={faSpinner} spin className="text-2xl" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          <FontAwesomeIcon icon={faCheck} className="text-3xl mb-2 block" />
          No feedback in this filter.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const catM  = CAT_META[item.category] ?? CAT_META.general;
            const statM = STATUS_META[item.adminStatus] ?? STATUS_META.new;
            return (
              <div key={item._id} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${catM.bg} ${catM.color}`}>
                      <FontAwesomeIcon icon={catM.icon} />
                      {catM.label}
                    </span>
                    {item.rating && (
                      <span className="text-xs text-amber-500 font-semibold">
                        {"★".repeat(item.rating)}{"☆".repeat(5 - item.rating)}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statM.cls}`}>
                    {statM.label}
                  </span>
                </div>

                {/* Feature tags */}
                {item.featureRequests?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {item.featureRequests.map((f) => (
                      <span key={f} className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-full">
                        {FEATURE_LABELS[f] ?? f}
                      </span>
                    ))}
                  </div>
                )}

                {/* Message */}
                {item.message && (
                  <p className="text-sm text-slate-700 italic bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                    "{item.message}"
                  </p>
                )}

                <div className="text-xs text-slate-400">{fmtDate(item.createdAt)}</div>

                {/* Admin note from previous review */}
                {item.adminNote && item.adminStatus !== "new" && (
                  <div className="text-xs text-slate-500 border-t border-slate-100 pt-2">
                    Note: {item.adminNote}
                  </div>
                )}

                {/* Action */}
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <div className="flex gap-2">
                    <select
                      value={actionStatus[item._id] ?? item.adminStatus}
                      onChange={(e) => setActionStatus((prev) => ({ ...prev, [item._id]: e.target.value }))}
                      className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      {Object.entries(STATUS_META).map(([v, m]) => (
                        <option key={v} value={v}>{m.label}</option>
                      ))}
                    </select>
                    <button
                      disabled={actioningId === item._id}
                      onClick={() => updateFeedback(item._id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {actioningId === item._id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                      Save
                    </button>
                  </div>
                  <input
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    placeholder="Admin note (optional, shown internally)…"
                    value={notes[item._id] ?? item.adminNote ?? ""}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [item._id]: e.target.value }))}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="text-sm text-slate-600">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
            className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function StatusManager() {
  const [tab, setTab] = useState("qa");

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FontAwesomeIcon icon={faShield} className="text-indigo-500" />
          Status Manager
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          QA testing tool for posting/clearing statuses, moderation queue for user-reported content, and user feedback.
        </p>
      </div>

      <TabBar tab={tab} setTab={setTab} />

      {tab === "qa" ? <QATool /> : tab === "reports" ? <ReportsQueue /> : <FeedbackQueue />}
    </div>
  );
}
